// Centralized API helpers.
//
// CONFIGURATION AT RUNTIME, NOT AT BUILD
// --------------------------------------
// Create React App compiles every REACT_APP_* value into the bundle at
// `npm run build`, which would mean one image per environment. The Cloud Run
// pipeline (soar-inc/soar-cloudrun) builds ONE image and promotes that exact
// digest everywhere, so the two values this app needs arrive at container start
// instead: docker/40-runtime-env.sh writes /env.js from the container's
// environment, public/index.html loads it before the bundle, and the code below
// prefers `window.__ENV` over the compiled `process.env` value.
//
// The compiled value remains the fallback, so an image built by the legacy GKE
// workflow — which bakes REACT_APP_API_BASE_URL with a build arg and serves no
// /env.js — behaves exactly as it does today.
//
// ORDERING: /env.js must execute before this module. public/index.html loads it
// with a plain <script> in <head>; Create React App injects the application
// bundle at the end of <body> with `defer`, so the plain script always runs
// first. Moving the tag out of <head>, or giving it `defer`/`async`, breaks that
// guarantee — src/lib/api.test.ts pins the tag's presence for that reason.
//
// The Decision Engine API key is sent as the `x-api-key` header on every request.

export interface RuntimeEnv {
	REACT_APP_API_BASE_URL?: string;
	REACT_APP_DECISION_ENGINE_API_KEY?: string;
}

declare global {
	interface Window {
		__ENV?: RuntimeEnv;
	}
}

/**
 * The runtime configuration written by docker/40-runtime-env.sh, or an empty
 * object when the app runs somewhere that has none (`npm start`, jest, or an
 * image built by the legacy GKE workflow).
 */
export function runtimeEnv(): RuntimeEnv {
	if (typeof window === 'undefined') return {};
	return window.__ENV ?? {};
}

/**
 * The Decision Engine API key: the runtime value first, the compiled
 * build-time value as the fallback, and '' when neither is configured —
 * src/lib/auth.tsx refuses every login in that case, deliberately.
 */
export const DECISION_ENGINE_API_KEY =
	runtimeEnv().REACT_APP_DECISION_ENGINE_API_KEY || process.env.REACT_APP_DECISION_ENGINE_API_KEY || '';

/**
 * The Decision Engine base URL: the runtime value first, then the compiled
 * build-time value, then the caller's fallback (the existing localhost
 * defaults, which differ per call site and are preserved).
 */
export function getApiBaseUrl(fallback: string): string {
	return runtimeEnv().REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || fallback;
}

/**
 * Returns the default headers required for every Decision Engine API call.
 * Always includes the `x-api-key` header. Any extra headers passed in are merged
 * on top (and win on conflicts).
 */
export function buildApiHeaders(extra?: HeadersInit): HeadersInit {
	return {
		'x-api-key': DECISION_ENGINE_API_KEY,
		...(extra as Record<string, string> | undefined),
	};
}

/**
 * A thin wrapper around `fetch` that automatically attaches the `x-api-key`
 * header to every request. Use this instead of calling `fetch` directly for any
 * Decision Engine API endpoint.
 */
export function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
	return fetch(input, {
		...init,
		headers: buildApiHeaders(init.headers),
	});
}
